import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";
import { Button, Dialog } from "@cmsgov/ds-medicare-gov";
import { useTranslate, sendTealiumEvent } from "../helpers";
import {
  ActionType,
  Plan,
  PlanType,
  SearchResultPlan,
  PlanEnrollmentStatus,
} from "../@types";
import { AppStore } from "../app/store";
import { useFlags } from "launchdarkly-react-client-sdk";
import { isCurrentPlan } from "../helpers/beneficiaryInfoHelpers";
import { RouteComponentProps, withRouter } from "react-router";
import routes from "../app/routes";

export interface EnrollOptions extends RouteComponentProps {
  plan: Plan | SearchResultPlan;
  aria_string?: string;
  onClick?: () => void;
  tealiumEventAction?: string;
}

const EnrollButtonForm: FunctionComponent<EnrollOptions> = ({
  plan,
  plan: {
    name,
    plan_id,
    contract_id,
    contract_year,
    segment_id,
    low_performing,
    enrollment_status,
  },
  onClick,
  history,
  aria_string,
  tealiumEventAction,
}) => {
  const {
    mctYpPublicPreview,
    feDisableHpmsEnrollment,
    mctCurrentPlanYear,
  } = useFlags();

  const { state, dispatch } = useContext(AppStore.AppContext);
  const [showTransitionModal, setTransitionModalVisibility] = useState(false);
  const t = useTranslate();

  useEffect(() => {
    if (showTransitionModal) {
      const dialogWrapper: HTMLElement | null = document.querySelector(
        ".ds-c-dialog-wrap"
      );

      if (dialogWrapper) {
        dialogWrapper.scrollTop = 0;
      }
    }
  }, [showTransitionModal]);

  if (low_performing) {
    return (
      <p className="ds-u-text-align--center">
        {t("plan_card.contact_plan_to_enroll")}
      </p>
    );
  }

  const openEnrollModal = (): void => {
    setTransitionModalVisibility(true);
  };

  const isCurrPlan = isCurrentPlan(state.currentCoverage, {
    plan_id,
    contract_id,
    contract_year,
    segment_id,
  });

  const isCurrPlanForNextYear = isCurrentPlan(state.nextYearCoverage, {
    plan_id,
    contract_id,
    contract_year,
    segment_id,
  });

  const EnrollButton = (): JSX.Element => {
    if (isCurrPlan && !state.csr) {
      return (
        <p className="ds-u-text-align--center">
          {t("plan_card.your_current_plan")}
        </p>
      );
    }

    if (isCurrPlanForNextYear && !state.csr) {
      return (
        <p className="ds-u-text-align--center">
          {t("plan_card.your_next_plan")}
        </p>
      );
    }

    const planFrozen = plan.redactions.some(
      r => r.type === "frozen" && r.issue === "frzn_enrlmnt"
    );
    const contactPlan = !plan.enrollment_opt_in_status || planFrozen;

    if (contactPlan) {
      return <p>{t("plan_card.contact_plan_to_enroll")}</p>;
    } else if (enrollment_status === PlanEnrollmentStatus.NOT_NOW) {
      return <p>{t("plan_enroll.not_accepting")}</p>;
    }

    if (feDisableHpmsEnrollment) {
      return (
        <p className="ds-u-font-style--italic">
          {t("plan_enroll.cannot_process_enrollments")}
        </p>
      );
    }

    return (
      <Button
        type="button"
        variation="primary"
        aria-label={aria_string}
        className="e2e-plan-enroll mct-hide-print EnrollButton__button"
        onClick={(): void => {
          if (onClick) {
            onClick();
          }
          openEnrollModal();
        }}
      >
        {t("app.terms.enroll")}
      </Button>
    );
  };

  return (
    <div className="EnrollTransitionDialog PlanCard__mobile_order_2">
      {showTransitionModal && (
        <Dialog
          size="wide"
          className="EnrollTransitionDialog__dialog"
          headerClassName="EnrollTransitionDialog__header"
          getApplicationNode={(): HTMLElement | null =>
            document.getElementById("root")
          }
        >
          <div className="EnrollTransitionDialog__content">
            <h1 className="ds-h4 ds-u-margin-bottom--0">
              {plan.contract_year == mctCurrentPlanYear + 1 ? (
                <FormattedMessage
                  id="plan_enroll.you_are_joining_with_year"
                  values={{ year: plan.contract_year }}
                />
              ) : (
                t("plan_enroll.you_are_joining")
              )}
            </h1>
            <div className="EnrollTransitionDialog__planDetails">
              <h2 id="dialog-title" className="ds-h2 ds-u-margin--0">
                {name}
              </h2>
              <h3 className="ds-h3 ds-u-margin--0 ds-u-color--gray">
                {`${t(
                  "plan_terms.plan_id"
                )} ${contract_id}-${plan_id}-${segment_id}`}
              </h3>
            </div>
          </div>
          <div className="EnrollTransitionDialog__content">
            <b>{t("enroll_transition.checklist_heading")}:</b>
            <FormattedHTMLMessage
              id={
                plan.contract_year === "2021"
                  ? "enroll_transition.2021.checklist"
                  : "enroll_transition.checklist"
              }
            />
          </div>
          <div className="EnrollTransitionDialog__content ds-u-display--flex ds-u-justify--start">
            <span className="fas fa-lock ds-u-margin-right--1" />
            <span className="ds-u-font-size--small ds-u-color--gray">
              {t(
                plan.contract_year === "2021"
                  ? "enroll_transition.2021.security_advisory"
                  : "enroll_transition.security_advisory"
              )}
            </span>
          </div>

          <div className="EnrollTransitionDialog__actions">
            <Button
              className="EnrollTransitionDialog__cancelButton"
              key="cancel"
              onClick={(): void => {
                setTransitionModalVisibility(false);
                sendTealiumEvent(tealiumEventAction || "Modal", "Go Back");
              }}
            >
              {t(
                plan.contract_year === "2021"
                  ? "enroll_transition.2021.go_back"
                  : "plan_enroll.cancel"
              )}
            </Button>

            <Button
              variation="primary"
              className="e2e-submit-enrollment-form"
              key="primary"
              onClick={(): void => {
                dispatch({
                  type: ActionType.UPDATE_SELECTED_PLAN,
                  payload: plan,
                });
                history.push(routes.enroll);
                setTransitionModalVisibility(false);
                sendTealiumEvent(
                  tealiumEventAction || "Modal",
                  `Join Plan - ${plan.name} - ${
                    plan.plan_type === PlanType.PDP ? "PDP" : "MA/MAPD"
                  }`
                );
              }}
            >
              {t("plan_enroll.join_plan")}
            </Button>
          </div>

          {state.viewMedigap && (
            <div className="EnrollTransitionDialog__medigap">
              <h2 className="ds-h3">
                {t(
                  plan.contract_year === "2021"
                    ? "enroll_transition.2021.medigap_header"
                    : "enroll_transition.medigap_header"
                )}
              </h2>
              <p>
                {t(
                  plan.contract_year === "2021"
                    ? "enroll_transition.2021.medigap"
                    : "enroll_transition.medigap"
                )}
              </p>
            </div>
          )}
        </Dialog>
      )}

      {mctYpPublicPreview && (
        <>
          {plan.contract_year === (mctCurrentPlanYear + 1).toString() && (
            <p className="e2e-enroll-begins">
              {t("public_preview.enroll_begins")}
            </p>
          )}

          {plan.contract_year === mctCurrentPlanYear.toString() && (
            <EnrollButton />
          )}
        </>
      )}

      {!mctYpPublicPreview && <EnrollButton />}

      {isCurrPlan && (
        <p>
          <FormattedHTMLMessage id="current_plan.current_coverage_disclaimer" />
        </p>
      )}
    </div>
  );
};

export default withRouter(EnrollButtonForm);
